import apiClient from './apiClient';
import { API_CONFIG } from '../config';

const STORAGE_KEYS = {
  USER: 'auth.user'
};

function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(payload);
    return JSON.parse(decoded);
  } catch (err) {
    return null;
  }
}

class AuthService {
  constructor() {
    this.currentUser = null;
    this.isLoggedIn = false;
    this.loadSession();
  }

  loadSession() {
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    if (storedUser) {
      try {
        this.currentUser = JSON.parse(storedUser);
        this.isLoggedIn = true;
      } catch (err) {
        localStorage.removeItem(STORAGE_KEYS.USER);
      }
    }
  }

  setSession({ accessToken, refreshToken, user }) {
    apiClient.setTokens(accessToken, refreshToken || null);

    if (user) {
      this.currentUser = user;
      this.isLoggedIn = true;
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      return;
    }

    const decoded = accessToken ? decodeJwtPayload(accessToken) : null;
    this.currentUser = decoded
      ? {
          id: decoded.sub,
          email: decoded.email,
          role: decoded.role
        }
      : null;
    this.isLoggedIn = Boolean(this.currentUser);

    if (this.currentUser) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(this.currentUser));
    }
  }

  clearSession() {
    this.currentUser = null;
    this.isLoggedIn = false;
    localStorage.removeItem(STORAGE_KEYS.USER);
    apiClient.clearTokens();
  }

  async signup(userData) {
    const payload = {
      name: userData.fullName || userData.name,
      email: userData.email,
      password: userData.password,
      role: (userData.userType || userData.role || 'user').toLowerCase()
    };

    const signupResponse = await apiClient.post(API_CONFIG.ENDPOINTS.USERS.REGISTER, payload);

    // Auto-login right after account creation to establish an authenticated session.
    const loginResult = await this.login({
      email: payload.email,
      password: payload.password
    });

    return {
      success: true,
      message: 'Registration successful. User logged in.',
      data: signupResponse.data,
      user: loginResult.user,
      accessToken: loginResult.accessToken,
      refreshToken: loginResult.refreshToken
    };
  }

  async login(credentials) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.LOGIN, {
      email: credentials.email,
      password: credentials.password
    });

    const data = response.data || {};
    const accessToken = data.accessToken || data.token;
    const refreshToken = data.refreshToken || null;

    this.setSession({
      accessToken,
      refreshToken,
      user: data.user || null
    });

    return {
      success: true,
      message: 'Login successful',
      user: this.currentUser,
      accessToken,
      refreshToken
    };
  }

  async refreshSession() {
    if (!apiClient.refreshToken) {
      return null;
    }

    const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.REFRESH, {
      refreshToken: apiClient.refreshToken
    });

    const data = response.data || {};
    if (data.accessToken) {
      this.setSession({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || apiClient.refreshToken,
        user: this.currentUser
      });
    }

    return data;
  }

  async logout() {
    if (apiClient.refreshToken) {
      try {
        await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.LOGOUT, {
          refreshToken: apiClient.refreshToken
        });
      } catch (err) {
      }
    }

    this.clearSession();
    return { success: true, message: 'Logged out successfully' };
  }

  getCurrentUser() {
    if (!this.currentUser) {
      this.loadSession();
    }
    return this.currentUser;
  }

  isAuthenticated() {
    return this.isLoggedIn || Boolean(this.getCurrentUser());
  }
}

const authService = new AuthService();
export default authService;
